/**
 * « Mot de passe oublié » — logique sans React ni Supabase, vérifiée par
 * `node scripts/check-password-recovery.mjs`.
 *
 * Le parcours :
 *   1. l'écran de connexion demande un lien (`resetPassword`) ; l'adresse de retour est
 *      `/auth?reset=1`, plus `&from=app` si la demande part de l'app installée ;
 *   2. le lien de l'email ramène sur `/auth?reset=1…#access_token=…&type=recovery` : la
 *      librairie Supabase ouvre une session de récupération, puis EFFACE le `#…` de
 *      l'adresse. D'où la lecture de l'adresse dans `supabase.ts`, avant `createClient` ;
 *   3. l'écran « Choisis ton nouveau mot de passe » s'affiche tant que la récupération
 *      est en attente pour CE compte (mémoire de l'onglet, `sessionStorage`) ;
 *   4. un lien expiré ou déjà servi revient avec `#error_code=otp_expired…`, que la
 *      librairie laisse dans l'adresse : écran « Ce lien ne fonctionne plus ».
 *
 * Cas iPhone : l'email s'ouvre dans le navigateur, jamais dans l'app installée (qui a son
 * propre stockage, donc pas la session). `from=app` permet de le reconnaître et de dire au
 * client de revenir dans l'app — rien ne permet de l'ouvrir depuis le navigateur.
 */

/** Règle de l'inscription, reprise telle quelle partout où un mot de passe est choisi. */
export const MIN_PASSWORD_LENGTH = 6
export const MIN_PASSWORD_HINT = `${MIN_PASSWORD_LENGTH} caractères minimum`

/**
 * Durée de validité du lien annoncée au client. Doit correspondre au réglage Supabase
 * « Email OTP Expiration » (3600 s) — à revoir si ce réglage change.
 */
export const RESET_LINK_VALIDITY = "1 heure"
const RESET_LINK_VALIDITY_MS = 60 * 60 * 1000

// ============================================
// Lecture de l'adresse de retour
// ============================================

export type AuthRedirect =
  | { kind: "none" }
  | { kind: "recovery"; userId: string | null }
  | { kind: "error"; code: string }

/**
 * Ce que Supabase a mis dans l'adresse. L'erreur peut arriver dans le `#` (actuel) ou
 * dans la query string (anciennes versions de l'API Auth) : on lit les deux.
 */
export function parseAuthRedirect(search: string, hash: string): AuthRedirect {
  const h = new URLSearchParams(hash.replace(/^#/, ""))
  const q = new URLSearchParams(search.replace(/^\?/, ""))
  const read = (k: string) => h.get(k) ?? q.get(k)

  if (read("error_code") || read("error_description") || read("error")) {
    return { kind: "error", code: read("error_code") ?? read("error") ?? "unspecified" }
  }
  const token = h.get("access_token")
  if (h.get("type") === "recovery" && token) {
    return { kind: "recovery", userId: jwtSubject(token) }
  }
  return { kind: "none" }
}

/** `sub` du jeton d'accès, sans vérifier la signature (le serveur la vérifie). */
export function jwtSubject(token: string): string | null {
  try {
    const part = token.split(".")[1]
    if (!part) return null
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(part.length / 4) * 4, "=")
    const sub = JSON.parse(atob(b64))?.sub
    return typeof sub === "string" && sub ? sub : null
  } catch {
    return null
  }
}

export function isResetUrl(pathname: string, search: string): boolean {
  return pathname === "/auth" && new URLSearchParams(search).get("reset") === "1"
}

export function isFromApp(search: string): boolean {
  return new URLSearchParams(search).get("from") === "app"
}

export function resetRedirectPath(fromApp: boolean): string {
  return `/auth?reset=1${fromApp ? "&from=app" : ""}`
}

// ============================================
// Quel écran afficher sur /auth
// ============================================

/** Récupération en attente dans cet onglet : le lien a été ouvert, rien n'est encore choisi. */
export interface RecoveryPending {
  /** Compte du lien (`null` si le jeton n'a pas pu être lu : le formulaire n'est alors jamais proposé). */
  userId: string | null
  /** Le lien a été demandé depuis l'app installée. */
  fromApp: boolean
}

export type RecoveryScreen = "new-password" | "link-expired" | "other-account"

export function recoveryScreen(input: {
  /** Lu dans l'adresse au chargement de la page. */
  redirect: AuthRedirect
  isResetUrl: boolean
  pending: RecoveryPending | null
  /** Compte connecté dans cet onglet. */
  userId: string | null
}): RecoveryScreen | null {
  const { redirect, pending, userId } = input
  // Un lien refusé à l'instant prime : c'est celui sur lequel le client vient de toucher.
  if (input.isResetUrl && redirect.kind === "error") return "link-expired"
  // Le formulaire seulement si la session ouverte est CELLE DU LIEN : sinon il changerait le
  // mot de passe d'un autre compte (navigateur déjà connecté ailleurs).
  if (pending && pending.userId !== null && pending.userId === userId) return "new-password"
  if (input.isResetUrl && redirect.kind === "recovery") {
    // Lien lu, mais aucune session ouverte : le serveur l'a refusé.
    if (!userId) return "link-expired"
    // Lien lu, mais la session ouverte est celle d'un autre compte (celle du lien n'a pas
    // été appliquée), ou le jeton est illisible : ni formulaire, ni renvoi silencieux sur
    // l'accueil de l'autre compte. Constaté le 05/10 (test B″, navigateur connecté en admin).
    return "other-account"
  }
  return null
}

/**
 * Après l'enregistrement : demandé depuis l'app installée mais ouvert dans le navigateur
 * → on dit au client de revenir dans l'app ; sinon → accueil.
 */
export function afterPasswordChange(input: { fromApp: boolean; standalone: boolean }): "back-to-app" | "home" {
  return input.fromApp && !input.standalone ? "back-to-app" : "home"
}

// ============================================
// Saisie du nouveau mot de passe
// ============================================

export function validateNewPassword(password: string, confirm: string) {
  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH
  const mismatch = confirm.length > 0 && confirm !== password
  return {
    tooShort,
    mismatch,
    canSubmit: password.length >= MIN_PASSWORD_LENGTH && confirm === password,
  }
}

export type UpdatePasswordError = "same_password" | "weak_password" | "session" | "rate_limit" | "generic"

/** Erreur de `supabase.auth.updateUser({ password })` → cas affiché au client. */
export function classifyUpdatePasswordError(err: {
  code?: string | null
  name?: string | null
  status?: number | null
  message?: string | null
}): UpdatePasswordError {
  const code = err.code ?? ""
  const msg = (err.message ?? "").toLowerCase()
  if (code === "same_password" || msg.includes("different from the old password")) return "same_password"
  if (code === "weak_password" || err.name === "AuthWeakPasswordError") return "weak_password"
  if (
    err.name === "AuthSessionMissingError" ||
    ["session_not_found", "session_expired", "bad_jwt", "reauthentication_needed", "refresh_token_not_found"].includes(code) ||
    err.status === 401 || err.status === 403
  ) return "session"
  if (code.startsWith("over_") || err.status === 429 || msg.includes("rate limit")) return "rate_limit"
  return "generic"
}

export const UPDATE_PASSWORD_MESSAGES: Record<Exclude<UpdatePasswordError, "session">, string> = {
  same_password: "C'est déjà ton mot de passe actuel : tu peux te connecter avec, ou en choisir un autre.",
  weak_password: "Ce mot de passe est trop facile à deviner. Choisis-en un autre.",
  rate_limit: "Trop de demandes d'affilée. Réessaie dans quelques minutes.",
  generic: "Je n'ai pas pu enregistrer ton mot de passe. Vérifie ta connexion et réessaie.",
}

/**
 * Erreur de `resetPasswordForEmail` → message affiché.
 *
 * Supabase ne cherche à envoyer que si l'adresse a un compte (sinon : succès, sans rien
 * envoyer). Les erreurs ci-dessous ne surviennent donc que pour un compte existant.
 */
export function resetRequestErrorMessage(err: { code?: string | null; status?: number | null; message?: string | null }): string {
  const code = err.code ?? ""
  const msg = (err.message ?? "").toLowerCase()
  if (code.startsWith("over_") || err.status === 429 || msg.includes("rate limit") || msg.includes("security purposes")) {
    return "Trop de demandes d'affilée. Réessaie dans quelques minutes."
  }
  // Adresse refusée au moment de l'envoi : domaine sans serveur de messagerie, faute de
  // frappe enregistrée à l'inscription… Constaté le 05/10 sur une adresse `@example.com`.
  // Réessayer n'y changera rien : on le dit.
  if (code === "email_address_invalid") {
    return "Je n'arrive pas à envoyer d'email à cette adresse. Vérifie qu'elle est bien écrite ; si c'est la bonne, écris-moi et je t'aide à retrouver l'accès."
  }
  // Service d'email intégré de Supabase : adresse hors de l'équipe du projet (limite
  // annoncée par Supabase, levée par le chantier C, SMTP dédié).
  if (code === "email_address_not_authorized") {
    return "Je n'arrive pas à t'envoyer d'email pour le moment. Écris-moi et je t'aide à retrouver l'accès."
  }
  if (msg.includes("network") || msg.includes("fetch")) return "Problème de connexion réseau. Réessaie."
  return "Je n'ai pas pu envoyer le lien. Réessaie dans quelques minutes ; si ça recommence, écris-moi."
}

// ============================================
// Ce qui est gardé sur l'appareil
// ============================================

/** Demande de lien faite depuis cet appareil (pré-remplit l'email, garde l'encadré affiché). */
export interface ResetRequest {
  email: string
  fromApp: boolean
  at: number
}

export function readResetRequest(raw: string | null, now: number): ResetRequest | null {
  if (!raw) return null
  try {
    const r = JSON.parse(raw)
    if (typeof r?.email !== "string" || !r.email || typeof r.at !== "number") return null
    if (now - r.at > RESET_LINK_VALIDITY_MS || now < r.at) return null
    return { email: r.email, fromApp: r.fromApp === true, at: r.at }
  } catch {
    return null
  }
}

export function readPending(raw: string | null): RecoveryPending | null {
  if (!raw) return null
  try {
    const p = JSON.parse(raw)
    if (!p || typeof p !== "object") return null
    return { userId: typeof p.userId === "string" ? p.userId : null, fromApp: p.fromApp === true }
  } catch {
    return null
  }
}

// Accès au stockage : peut lever (navigation privée, stockage bloqué) → jamais bloquant.
const PENDING_KEY = "zeyone_password_recovery" // sessionStorage : propre à l'onglet du lien
const REQUEST_KEY = "zeyone_password_reset_request" // localStorage : survit à la fermeture de l'app

export function loadPending(): RecoveryPending | null {
  try { return readPending(window.sessionStorage.getItem(PENDING_KEY)) } catch { return null }
}
export function savePending(p: RecoveryPending): void {
  try { window.sessionStorage.setItem(PENDING_KEY, JSON.stringify(p)) } catch { /* ignore */ }
}
export function clearPending(): void {
  try { window.sessionStorage.removeItem(PENDING_KEY) } catch { /* ignore */ }
}

export function loadResetRequest(): ResetRequest | null {
  try { return readResetRequest(window.localStorage.getItem(REQUEST_KEY), Date.now()) } catch { return null }
}
export function saveResetRequest(email: string, fromApp: boolean): ResetRequest {
  const r: ResetRequest = { email, fromApp, at: Date.now() }
  try { window.localStorage.setItem(REQUEST_KEY, JSON.stringify(r)) } catch { /* ignore */ }
  return r
}
export function clearResetRequest(): void {
  try { window.localStorage.removeItem(REQUEST_KEY) } catch { /* ignore */ }
}
